import { z } from 'zod';

/**
 * What an Email Verification step stores in `WorkflowStep.Settings`.
 *
 * Only the parts a tenant can author: the line that appears above the code in the email, and how
 * long the code stays usable. Everything security-relevant — code length, attempt cap, the resend
 * cool-down, the hashing — is fixed in the backend and deliberately NOT configurable here.
 *
 * Mirrors `questionnaireStepSettings.ts`, including the merge-preserving write: the `Settings` blob
 * is shared with the generic Settings tab and the advanced-rules grouping, so anything writing it
 * must not clobber keys it does not own.
 *
 * KEYS MUST MATCH `EmailVerificationStepSettings.cs`, which reads them back out of the run's frozen
 * snapshot. They are prefixed rather than nested because the blob is flat and shared — the same
 * convention `questionnaireId` follows.
 */
export const emailVerificationStepSettingsSchema = z.object({
  emailVerificationMessage: z.string().nullable().default(null),
  emailVerificationValidityMinutes: z.number().nullable().default(null),
});

export type EmailVerificationStepSettings = z.infer<typeof emailVerificationStepSettingsSchema>;

/**
 * Defaults, duplicated from `EmailVerificationStepSettings.cs`.
 *
 * They have to be stated on both sides: the server needs them for a step saved before this panel
 * existed, and the panel needs them to show what an unconfigured step will actually send. If you
 * change one, change the other — they are asserted equal by nothing but this comment.
 */
export const DEFAULT_EMAIL_MESSAGE =
  'Enter the code below on the verification page to confirm this email address belongs to you.';

export const DEFAULT_VALIDITY_MINUTES = 10;
export const MIN_VALIDITY_MINUTES = 1;
export const MAX_VALIDITY_MINUTES = 60;

/** Longest message the panel accepts. Long enough for two sentences, short enough to stay a line. */
export const MAX_EMAIL_MESSAGE_LENGTH = 300;

const EMPTY: EmailVerificationStepSettings = {
  emailVerificationMessage: null,
  emailVerificationValidityMinutes: null,
};

/**
 * Reads the email settings out of a step's raw settings JSON.
 *
 * Tolerant by design: the blob carries every other step type's settings too, and a step may predate
 * this feature. Anything unparseable reads as "nothing configured", which the panel then shows as
 * the defaults — rather than throwing inside the workflow editor.
 */
export function parseEmailVerificationStepSettings(
  raw: string | null,
): EmailVerificationStepSettings {
  if (!raw) return EMPTY;

  try {
    const parsed = emailVerificationStepSettingsSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : EMPTY;
  } catch {
    return EMPTY;
  }
}

/**
 * Merges email settings back into a step's settings JSON, preserving every key this feature does not
 * own.
 */
export function writeEmailVerificationStepSettings(
  raw: string | null,
  next: Partial<EmailVerificationStepSettings>,
): string {
  let base: Record<string, unknown> = {};
  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') base = parsed as Record<string, unknown>;
    } catch {
      /* unparseable — start clean rather than lose the new settings */
    }
  }

  return JSON.stringify({ ...base, ...next });
}
